import { Alert, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { itemsOf, listInstallments, type PAInstallment, type PAInstallmentStep } from '../api/money';
import { formatCivilDate, formatInstantDay } from '../format';
import { Money } from '../money/Money';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';

/**
 * «Рассрочка» — Appendix L §20, view-only («The Subscriber can only view (not create)
 * installment plans»).
 *
 * ============================================================================
 * ⚠ THE LABEL ON THIS SCREEN IS A CORRECTNESS QUESTION, NOT A WORDING ONE
 * ============================================================================
 *
 * `remaining_amount` is a **decaying SHIELD and not a debt** (Appendix M §3.18 «M3», which
 * STRUCK the earlier claim that an installment «is paid down through ordinary payments»). The
 * FULL price was debited at assignment; what remains is calendar-driven protection and the
 * FOURTH TERM OF AVAILABILITY. A payment never repays it.
 *
 * So calling it «осталось выплатить» would tell the subscriber he owes money that was taken
 * from him on day one, and he would pay it twice in all but name. The row is therefore labelled
 * as SHELTER, with the price he already paid beside it, and the hint says so in one sentence.
 * This is the one screen in the wave where the correct figure with the wrong label is worse
 * than no screen.
 *
 * A CLOSED plan is listed too — decayed to zero, or paid off early — because §20 is a history
 * view and a plan that finished last month is exactly what a subscriber checks. `live`
 * distinguishes them.
 */
export function InstallmentsScreen() {
  return (
    <MoneyPage titleKey="installments.title" leadKey="installments.lead">
      {(contract) => <InstallmentsBody contractID={contract.id} currency={contract.currency} />}
    </MoneyPage>
  );
}

function InstallmentsBody({ contractID, currency }: { contractID: number; currency: string }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAInstallment[]>(
    async () => itemsOf(await listInstallments(contractID)),
    [contractID],
  );

  return (
    <QueryFrame query={query} reload={reload}>
      {(rows) =>
        rows.length === 0 ? (
          <Alert type="info" showIcon message={t('installments.empty')} />
        ) : (
          <>
            {rows.map((plan) => (
              <InstallmentCard key={plan.id} plan={plan} currency={currency} t={t} />
            ))}
          </>
        )
      }
    </QueryFrame>
  );
}

function InstallmentCard({
  plan,
  currency,
  t,
}: {
  plan: PAInstallment;
  currency: string;
  t: TFunction;
}) {
  const steps: ReadonlyArray<Column<PAInstallmentStep>> = [
    {
      key: 'due',
      labelKey: 'installments.columnDue',
      // A CIVIL date, sliced rather than parsed: a UTC midnight would move the 1st of the
      // month to the 30th for a reader west of the meridian.
      render: (s) => formatCivilDate(s.due_date),
    },
    {
      key: 'amount',
      labelKey: 'installments.columnAmount',
      className: 'pa-td-right',
      render: (s) => <Money minor={s.amount} currency={currency} />,
    },
    {
      key: 'applied',
      labelKey: 'installments.columnApplied',
      render: (s) =>
        s.applied
          ? t('installments.appliedAt', { when: formatInstantDay(s.applied_at) })
          : t('installments.pending'),
    },
  ];

  return (
    <Card
      className="pa-block"
      title={plan.subject || plan.plan_name}
      extra={
        <span className={`pa-status ${plan.live ? 'pa-status-ok' : ''}`}>
          {plan.live ? t('installments.live') : t('installments.closed')}
        </span>
      }
    >
      <FactList
        facts={[
          {
            key: 'plan',
            label: t('installments.plan'),
            value: plan.plan_name,
            hint: plan.duration_months
              ? t('installments.duration', { months: plan.duration_months })
              : undefined,
          },
          {
            key: 'price',
            label: t('installments.price'),
            value: <Money minor={plan.price_amount} currency={currency} />,
            hint: t('installments.priceHint'),
          },
          {
            key: 'shield',
            label: t('installments.shield'),
            value: <Money minor={plan.remaining_amount} currency={currency} strong />,
            hint: t('installments.shieldHint'),
          },
          {
            key: 'assigned',
            label: t('installments.assigned'),
            value: formatInstantDay(plan.assigned_at),
            hint: plan.closed_at
              ? t('installments.closedAt', { when: formatInstantDay(plan.closed_at) })
              : undefined,
          },
        ]}
      />
      <h3 className="pa-section-subtitle">{t('installments.scheduleTitle')}</h3>
      <DataTable
        columns={steps}
        rows={plan.steps}
        rowKey={(s) => s.seq}
        emptyKey="installments.noSteps"
      />
    </Card>
  );
}
